import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

export interface UpsAuthContextValue {
  token: string | null;
  isAuthorized: boolean;
  authorize: (rawToken: string) => void;
  clear: () => void;
}

const UpsAuthContext = createContext<UpsAuthContextValue | undefined>(undefined);

// React memory only — no localStorage/sessionStorage/backend persistence.
// A refresh, new tab, or navigation away loses it; Clear removes it
// immediately. Never log the token.
export const UpsAuthProvider = ({ children }: PropsWithChildren<{}>) => {
  const [token, setToken] = useState<string | null>(null);

  const authorize = useCallback((rawToken: string) => {
    setToken(rawToken.trim());
  }, []);

  const clear = useCallback(() => setToken(null), []);

  const value = useMemo<UpsAuthContextValue>(
    () => ({ token, isAuthorized: token !== null, authorize, clear }),
    [token, authorize, clear],
  );

  return <UpsAuthContext.Provider value={value}>{children}</UpsAuthContext.Provider>;
};

export function useUpsAuthContext(): UpsAuthContextValue {
  const ctx = useContext(UpsAuthContext);
  if (!ctx) {
    throw new Error('useUpsAuthContext must be used within a UpsAuthProvider');
  }
  return ctx;
}
